// Preview API Client for fetching catalog items

import { debugConsole } from './debug';

// Preview page size constant (must match backend PreviewPageSize)
export const PREVIEW_PAGE_SIZE = 20;

interface PreviewItem {
  id: string;
  type: 'movie' | 'series' | 'channel' | 'tv';
  name: string;
  poster?: string;
  year?: number;
  imdbId?: string;
}

export class PreviewAPI {
  private baseURL: string;

  constructor(baseURL: string = '') {
    this.baseURL = baseURL;
  }

  async fetchCatalogItems(
    configHash: string,
    _catalogType: string,
    catalogId: string,
    _skip: number = 0,
    signal?: AbortSignal
  ): Promise<{ items: PreviewItem[]; hasMore: boolean }> {
    const url = `${this.baseURL}/api/preview/${configHash}/${catalogId}`;

    debugConsole.log(`[PreviewAPI] 🌐 Fetching catalog items from NEW ENDPOINT: /api/preview`);
    debugConsole.log(`[PreviewAPI]   - Full URL: ${url}`);
    debugConsole.log(`[PreviewAPI]   - catalogId: ${catalogId}`);
    debugConsole.log(`[PreviewAPI]   - configHash: ${configHash.substring(0, 20)}...`);
    debugConsole.log(`[PreviewAPI]   - Expected page size: ${PREVIEW_PAGE_SIZE}`);

    const startTime = Date.now();

    try {
      debugConsole.log(`[PreviewAPI] Sending fetch request...`);
      const response = await fetch(url, { signal });
      const fetchDuration = Date.now() - startTime;

      debugConsole.log(`[PreviewAPI] Response received in ${fetchDuration}ms`);
      debugConsole.log(`[PreviewAPI]   - status: ${response.status} ${response.statusText}`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      debugConsole.log(`[PreviewAPI] Parsing JSON response...`);
      const data = await response.json();
      const parseDuration = Date.now() - startTime;

      debugConsole.log(`[PreviewAPI] JSON parsed in ${parseDuration - fetchDuration}ms`);
      debugConsole.log(`[PreviewAPI]   - data.metas count: ${data.metas?.length || 0}`);

      // Convert Stremio catalog items to PreviewItems
      const items: PreviewItem[] = (data.metas || []).map((meta: any) => {
        // Parse year from releaseInfo (backend sends as string like "2024")
        let year: number | undefined;
        if (meta.releaseInfo) {
          const parsedYear = parseInt(meta.releaseInfo, 10);
          if (!isNaN(parsedYear)) {
            year = parsedYear;
          }
        }

        return {
          id: meta.id,
          type: meta.type,
          name: meta.name,
          poster: meta.poster,
          year: year,
          imdbId: meta.imdb_id
        };
      });

      const hasMore = data.metas && data.metas.length === PREVIEW_PAGE_SIZE;
      const totalDuration = Date.now() - startTime;

      debugConsole.log(`[PreviewAPI] ✅ Success - Loaded ${items.length} items in ${totalDuration}ms`);
      debugConsole.log(`[PreviewAPI]   - hasMore: ${hasMore}`);
      debugConsole.log(`[PreviewAPI]   - Page size check: ${data.metas?.length || 0} items (expected ${PREVIEW_PAGE_SIZE})`);
      if (items[0]) {
        debugConsole.log(`[PreviewAPI]   - sample item: ${items[0].name} (${items[0].type}, ${items[0].year || 'no year'})`);
      }

      return { items, hasMore };
    } catch (error: any) {
      const errorDuration = Date.now() - startTime;

      if (error.name === 'AbortError') {
        debugConsole.log(`[PreviewAPI] ⚠️ Request aborted after ${errorDuration}ms: ${url}`);
      } else {
        debugConsole.error(`[PreviewAPI] ❌ Failed to fetch after ${errorDuration}ms:`);
        debugConsole.error(`[PreviewAPI]   - error: ${error.message}`);
        debugConsole.error(`[PreviewAPI]   - stack: ${error.stack}`);
      }
      throw error;
    }
  }
}

export const previewAPI = new PreviewAPI();
